import React, { useCallback, useState } from 'react';
import { LogIn, UserPlus, ChevronDown } from 'lucide-react';
import { OPEN_BIR_SEAL_BADGE_EVENT } from 'utils/birSealBadgeEvent';
import styles from './LandingCTA.module.scss';
import landingCtaOffice from './landing-cta-office.png';

interface LandingCTAProps {
  onLogin: () => void;
  onRegister: () => void;
  isLoading?: boolean;
}

const faqs = [
  {
    q: 'Who is AbraFi for?',
    a: 'AbraFi is designed primarily for individuals, small business owners, freelancers, and bookkeepers who need a DIY solution to simplify their bookkeeping and generate PFRS-compliant reports without the compliance headache.',
  },
  {
    q: 'Does AbraFi handle my taxes?',
    a: 'AbraFi is a productivity tool that automates the generation of financial statements and records. While it significantly simplifies tax preparation, all final responsibilities for filings and accuracy remain with the user. We recommend reviewing reports with a professional if you have complex requirements.',
  },
  {
    q: 'Is it really free?',
    a: 'Yes, AbraFi is currently totally free to use during our active development phase. By registering now, you also lock in exclusive "Early Adopter" status, granting you massive discounts on our future subscription plans.',
  },
];

function LandingCTA({ onLogin, onRegister, isLoading }: LandingCTAProps) {
  const [openFaqs, setOpenFaqs] = useState<Record<number, boolean>>({});

  const toggleFaq = useCallback((index: number) => {
    setOpenFaqs((prev) => ({ ...prev, [index]: !prev[index] }));
  }, []);

  return (
    <>
      {/* FAQ */}
      <section
        id="faq"
        className={styles.faqSection}
        aria-labelledby="faq-heading"
      >
        <div className={styles.faqInner}>
          <h2 id="faq-heading" className={styles.faqTitle}>
            Common questions
          </h2>
          <div className={styles.faqList} role="list">
            {faqs.map(({ q, a }, index) => {
              const isOpen = Boolean(openFaqs[index]);
              const triggerId = `landing-faq-trigger-${index}`;
              const panelId = `landing-faq-panel-${index}`;
              return (
                <div key={q} className={styles.faqItem} role="listitem">
                  <div
                    className={`${styles.faqDetails} ${isOpen ? styles.faqDetailsOpen : ''}`}
                  >
                    <button
                      type="button"
                      id={triggerId}
                      className={styles.faqSummary}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggleFaq(index)}
                    >
                      <span>{q}</span>
                      <ChevronDown
                        size={18}
                        className={styles.faqChevron}
                        aria-hidden="true"
                      />
                    </button>
                    <div
                      id={panelId}
                      className={styles.faqAnswerPanel}
                      role="region"
                      aria-labelledby={triggerId}
                      aria-hidden={!isOpen}
                    >
                      <div className={styles.faqAnswerInner}>
                        <p className={styles.faqAnswer}>{a}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final Authority CTA */}
      <section className={styles.ctaSection} aria-labelledby="cta-heading">
        <div className={styles.ctaInner}>
          <div className={styles.ctaImageWrap} aria-hidden="true">
            <img
              src={landingCtaOffice}
              alt=""
              className={styles.ctaImage}
              loading="lazy"
            />
            <div className={styles.ctaImageOverlay} />
          </div>

          <div className={styles.ctaContent}>
            <h2 id="cta-heading" className={styles.ctaTitle}>
              Ready to simplify your business?
            </h2>
            <p className={styles.ctaDescription}>
              Register now in AbraFi to automate your bookkeeping—from receipt
              uploads to auto-generated PFRS-compliant financial reports.
            </p>
            <div className={styles.ctaActions}>
              <button
                className={styles.btnPrimary}
                onClick={onLogin}
                disabled={isLoading}
                type="button"
              >
                <LogIn size={18} aria-hidden="true" />
                Sign In
              </button>
              <button
                className={styles.btnSecondary}
                onClick={onRegister}
                disabled={isLoading}
                type="button"
              >
                <UserPlus size={18} aria-hidden="true" />
                Create Account
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer} role="contentinfo">
        <div className={styles.footerInner}>
          <div className={styles.footerBrand}>
            <img
              src={`${import.meta.env.BASE_URL}favicon-32x32.png`}
              alt=""
              className={styles.footerBrandLogo}
              aria-hidden="true"
              width={22}
              height={22}
            />
            <span className={styles.footerBrandName}>Abrasoft Hub</span>
          </div>
          <p className={styles.footerCopy}>
            &copy; {new Date().getFullYear()} Abrasoft I.T. Services. All rights
            reserved.
          </p>
          <div className={styles.footerLinks}>
            <button
              type="button"
              className={styles.footerSealLink}
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent(OPEN_BIR_SEAL_BADGE_EVENT),
                );
              }}
            >
              BIR &amp; NPC Seals
            </button>
            <span className={styles.footerTag}>Accounting</span>
            <span className={styles.footerTag}>Reports</span>
            <span className={styles.footerTag}>Kanban</span>
            <span className={styles.footerTag}>Storage</span>
          </div>
        </div>
      </footer>
    </>
  );
}

export default LandingCTA;
